/** Öffentliche Regal-Seite (`/r/:slug/:nummer`): ohne Login – Artikel mit Fotos und Preisen, Laden, Öffnungszeiten, Anfahrt, Kontakt. */
import { useEffect } from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { EmptyState, Icon, Notice, Seitenzustand } from "../ui";
import { Marke } from "../ui/Marke";
import { api, rufe } from "../api/client";
import { formatEuro } from "@shared/geld";
import { formatTag } from "@shared/zeit";
import { RechtlichesFuss } from "./Rechtliches";
import { oeffnungszeitenText } from "../mieter/api";

interface RegalSeiteDaten {
  laden: { name: string; slug: string; adresse: { strasse?: string; plz?: string; ort?: string }; telefon: string; oeffnungszeiten: Record<string, { von: string; bis: string } | null>; logo: boolean; akzent: string | null };
  anzeigename: string;
  mieternummer: string;
  regale: string[];
  lage: string;
  artikel: { id: string; bezeichnung: string; preisCent: number; kategorie: string; groesse: string; foto: boolean }[];
  aktionen: { titel: string; text: string; bis: string | null }[];
}

export function kartenLink(a: { strasse?: string; plz?: string; ort?: string }, name: string): string {
  const q = encodeURIComponent([name, a.strasse, [a.plz, a.ort].filter(Boolean).join(" ")].filter(Boolean).join(", "));
  return /iphone|ipad|ipod|macintosh/i.test(navigator.userAgent) ? `https://maps.apple.com/?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`;
}

export function RegalSeite() {
  const { slug = "", nummer = "" } = useParams();
  const q = useQuery({ queryKey: ["public", "regal", slug, nummer], queryFn: () => rufe<RegalSeiteDaten>(api.api.public.laden[":slug"].regal[":nummer"].$get({ param: { slug, nummer } })), retry: false });
  useEffect(() => {
    const akzent = q.data?.laden.akzent ?? null;
    if (akzent) document.documentElement.style.setProperty("--accent", akzent);
    document.title = q.data ? `${q.data.anzeigename} bei ${q.data.laden.name}` : "MeinRegalladen";
    return () => {
      document.documentElement.style.removeProperty("--accent");
    };
  }, [q.data]);

  return (
    <div className="mp" style={{ minHeight: "100dvh" }}>
      <Seitenzustand query={q} lines={6} thumb>
        {(d) => (
          <>
            <header className="sc-bar">
              <div className="sc-row">
                {d.laden.logo ? <img src={`/api/public/laden/${d.laden.slug}/logo`} alt="" className="app-logo" width={40} height={40} /> : <Marke size={40} />}
                <div className="sc-col" style={{ gap: 0, minWidth: 0 }}>
                  <span className="sc-bar__title">{d.laden.name}</span>
                  <span className="sc-muted sc-small">Regal {d.regale.join(", ")}{d.lage ? ` · ${d.lage}` : ""}</span>
                </div>
              </div>
            </header>
            <main className="mp__main">
              <section className="mp-hero">
                <span className="mp-hero__hallo">Regal von</span>
                <span className="mp-hero__betrag" style={{ fontSize: 32, lineHeight: "40px" }}>{d.anzeigename}</span>
                <span className="mp-hero__heute is-joy"><Icon name="sparkles" size={16} /> {d.artikel.length} {d.artikel.length === 1 ? "Artikel" : "Artikel"} im Laden</span>
              </section>
              {d.aktionen.length ? (
                <Notice tone="joy" icon="percent" title={d.aktionen.length === 1 ? d.aktionen[0]!.titel : "Aktionen in diesem Regal"}>
                  {d.aktionen.map((a) => `${a.text}${a.bis ? ` bis ${formatTag(a.bis)}` : ""}`).join(" · ")} – wird an der Kasse abgezogen.
                </Notice>
              ) : null}
              {d.artikel.length === 0 ? (
                <EmptyState illustration="empty-shelf" title="Gerade nichts im Regal">Schau bald wieder vorbei.</EmptyState>
              ) : (
                <div className="mp-raster">
                  {d.artikel.map((a) => (
                    <div key={a.id} className="mp-kachel-artikel" style={{ cursor: "default" }}>
                      {a.foto ? <img className="mp-kachel-artikel__bild" src={`/api/public/laden/${d.laden.slug}/regal/${d.mieternummer}/foto/${a.id}`} alt={a.bezeichnung} loading="lazy" /> : <span className="mp-kachel-artikel__bild"><Icon name="image-off" size={28} /></span>}
                      <span className="mp-kachel-artikel__preis">{formatEuro(a.preisCent)}</span>
                      <span className="mp-kachel-artikel__text">
                        <span className="mp-kachel-artikel__titel">{a.bezeichnung}</span>
                        {a.groesse || a.kategorie ? <span className="sc-muted sc-small">{[a.groesse, a.kategorie].filter(Boolean).join(" · ")}</span> : null}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <section className="sc-card">
                <h2 className="heading">So findest du uns</h2>
                <p className="sc-strong" style={{ margin: "var(--space-2) 0 0" }}>{d.laden.name}</p>
                <p className="sc-muted" style={{ margin: 0 }}>{[d.laden.adresse.strasse, [d.laden.adresse.plz, d.laden.adresse.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</p>
                {oeffnungszeitenText(d.laden.oeffnungszeiten).map((z) => <p key={z} className="sc-small" style={{ margin: "4px 0 0" }}>{z}</p>)}
                <div className="sc-toolbar" style={{ marginTop: "var(--space-3)" }}>
                  <a href={kartenLink(d.laden.adresse, d.laden.name)} target="_blank" rel="noreferrer" className="rg-btn rg-btn--primary">Anfahrt</a>
                  <a href={`/api/public/laden/${d.laden.slug}/kontakt.vcf`} className="rg-btn rg-btn--secondary">Kontakt speichern</a>
                  {d.laden.telefon ? <a href={`tel:${d.laden.telefon.replace(/\s/g, "")}`} className="rg-btn rg-btn--quiet">Anrufen</a> : null}
                </div>
              </section>
              <p className="sc-muted sc-small sc-center">Bezahlt wird im Laden. Preise können sich ändern. · MeinRegalladen</p>
              <RechtlichesFuss />
            </main>
          </>
        )}
      </Seitenzustand>
    </div>
  );
}
